/**
 * The colours a trip tells things apart by.
 *
 * Two things wear them. A person's colour is theirs for the whole trip: it draws their route on
 * the map, it backs their avatar everywhere their name appears, and it is the only thing that
 * answers "whose line is that?" at a glance. A tag's colour makes a wall of folders scannable
 * and a recording's badges tellable apart, because a colour is recognised before a name can be
 * read. One palette and not two: these values are contrast-tested against both surfaces, and a
 * second set of hues would be a second thing to keep legible for no gain.
 *
 * Stored as a name rather than a hex, so the palette can be restyled, given dark-mode values or
 * made more accessible without rewriting anybody's membership row or anybody's tags.
 *
 * Shared by the API and the web so the two cannot disagree about what "BLUE" is.
 */

/**
 * The pool, in the order it is handed out: the first member of a trip is blue, the second red,
 * the third green, the fourth yellow. Eight, because a trip with more people than that has
 * bigger problems than colour, and because past eight the hues stop being tellable apart.
 */
export const PALETTE = [
  "BLUE",
  "RED",
  "GREEN",
  "YELLOW",
  "PURPLE",
  "TEAL",
  "ORANGE",
  "PINK",
] as const;

export type PaletteColor = (typeof PALETTE)[number];

/**
 * One hex per colour per theme.
 *
 * Two tables rather than one because the page has a light and a dark surface and a single hex
 * cannot be legible on both: the value that clears 4.5:1 on white is too dark to read on slate,
 * and the one that reads on slate is too pale for white. The same split the project's own
 * `--good` and `--bad` tokens already make.
 *
 * A tint for an avatar's background is mixed from these in CSS rather than listed again, so the
 * two can never drift apart. The map is the exception: its tiles are the light style whatever
 * the page is wearing, so a route line always takes the light value.
 *
 * Every value is contrast-tested against the surface it is drawn on; `palette.test.ts` measures
 * them rather than trusting the choice.
 */
export const COLOR_HEX: Record<PaletteColor, string> = {
  BLUE: "#1d4ed8",
  RED: "#dc2626",
  GREEN: "#15803d",
  YELLOW: "#a16207",
  PURPLE: "#7c3aed",
  TEAL: "#0f766e",
  ORANGE: "#c2410c",
  PINK: "#db2777",
};

/** The same eight on the dark surface. */
export const COLOR_HEX_DARK: Record<PaletteColor, string> = {
  BLUE: "#60a5fa",
  RED: "#f87171",
  GREEN: "#4ade80",
  YELLOW: "#facc15",
  PURPLE: "#a78bfa",
  TEAL: "#2dd4bf",
  ORANGE: "#fb923c",
  PINK: "#f472b6",
};

/** Plain words for a picker; the name is the thing chosen, so it is also the thing shown. */
export const COLOR_LABEL: Record<PaletteColor, string> = {
  BLUE: "Blue",
  RED: "Red",
  GREEN: "Green",
  YELLOW: "Yellow",
  PURPLE: "Purple",
  TEAL: "Teal",
  ORANGE: "Orange",
  PINK: "Pink",
};

export const isPaletteColor = (v: unknown): v is PaletteColor =>
  typeof v === "string" && (PALETTE as readonly string[]).includes(v);

/**
 * The colour a member gets when they have not chosen one: their place in the join order. Wraps
 * past the end of the pool, so a ninth member is blue again rather than colourless — a repeat
 * is a smaller problem than a blank, and either of them can be fixed by choosing.
 *
 * The membership's rule alone. A tag has no computed default: its colour is picked once, when it
 * is made, and stored — a tag's place in a list moves as media arrives, and a colour that moves
 * with it would be no better than no colour at all.
 */
export function defaultColor(joinIndex: number): PaletteColor {
  const n = PALETTE.length;
  // Negative or fractional input is a caller's mistake, not a reason to hand back undefined.
  const i = Number.isFinite(joinIndex) ? Math.max(0, Math.floor(joinIndex)) : 0;
  return PALETTE[i % n]!;
}
